import React from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import themes from './HeaderTheme.module.css';
import styles from './MenuRow.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface MenuRowProps {
  /** Registry name of the 16px leading glyph. Omit for a row that leads with something custom. */
  icon?: string;
  label: string;
  /** Right-aligned hint, e.g. `⌘1`. */
  shortcut?: string;
  /** Painted with `--color-transparent-light-10`, the same fill hover uses. */
  selected?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  /** Replaces the shortcut slot — a check, a chevron, a count. */
  trailing?: React.ReactNode;
  /** Rendered instead of `icon`, for rows that lead with an avatar. */
  leading?: React.ReactNode;
  onClick?: () => void;
  theme?: HeaderTheme;
  /** Stories and visual tests only. */
  forceHover?: boolean;
  className?: string;
}

/**
 * One 32px row of a header menu: glyph, label, and either a shortcut or a trailing node.
 *
 * FORWARDS ITS REF to the button, which every menu but one ignores. The exception is the preview text
 * bar's size menu: fifteen stops is taller than the surface can be, so it opens scrolled to the size
 * the text already is, and `scrollIntoView` needs the element. A row that could not be reached would
 * have meant either scrolling the surface by arithmetic or opening a fifteen-item list at the top and
 * calling that acceptable.
 */
export const MenuRow = React.forwardRef<HTMLButtonElement, MenuRowProps>(function MenuRow({
  icon,
  label,
  shortcut,
  selected = false,
  destructive = false,
  disabled = false,
  trailing,
  leading,
  onClick,
  theme: themeProp,
  forceHover = false,
  className,
}: MenuRowProps, ref) {
  const theme = useV15Theme(themeProp);
  return (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      className={cn(
        themes[theme],
        styles.row,
        selected && styles.selected,
        destructive && styles.destructive,
        forceHover && styles.forceHover,
        className,
      )}
      disabled={disabled}
      onClick={onClick}
    >
      {leading ? (
        <span className={styles.glyph}>{leading}</span>
      ) : icon ? (
        <span className={styles.glyph}>
          <DeckIcon name={icon} size={16} />
        </span>
      ) : null}
      <span className={styles.label}>{label}</span>
      {trailing ? (
        <span className={styles.trailing}>{trailing}</span>
      ) : shortcut ? (
        <span className={styles.shortcut}>{shortcut}</span>
      ) : null}
    </button>
  );
});
